import type { Role } from '@sistemas/contracts'
import * as React from 'react'

import { ErrorState, Forbidden, Loading } from '@/components/states'
import {
  fetchBridgeSession,
  getIsSigningOut,
  redirectToBridgeLogin,
  signOutFromBridge,
} from '@/lib/bridge-auth'
import { clearRepairAttempt } from '@/lib/bridge-session-repair'
import { devSignOut, ensureDevLogin } from '@/lib/dev-auth'
import { appEnv } from '@/lib/env'
import { getSupabase } from '@/lib/supabase'

/**
 * Sessão do usuário, vinda do SSO Flexibase.
 *
 * O acesso tem DUAS etapas, e as duas precisam passar:
 *
 *   1. SSO — `GET /api/session` no spa-bridge diz quem é a pessoa e entrega o
 *      JWT que autoriza o PostgREST.
 *   2. Departamento — a pessoa precisa ter ficha ATIVA em `sistemas.member`.
 *      Entrar no SSO não é o mesmo que ser do Sistemas.
 *
 * INVIOLÁVEL: os papéis daqui servem apenas para ESCONDER o que a pessoa não
 * pode usar. A barreira real é o RLS (I6) — quem contorna a UI esbarra no
 * banco. Nunca tratar este estado como autorização.
 */

export interface SessionMember {
  readonly id: string
  readonly displayName: string
  readonly email: string
}

export interface SessionState {
  readonly status: 'carregando' | 'autenticado' | 'anonimo' | 'sem-acesso' | 'erro'
  readonly member: SessionMember | null
  readonly roles: readonly Role[]
  readonly signOut: () => Promise<void>
}

const SessionContext = React.createContext<SessionState | null>(null)

interface Resolved {
  status: SessionState['status']
  member: SessionMember | null
  roles: Role[]
  detail?: string
}

/**
 * Sobrescrita de papéis para conferir o RBAC sem trocar de conta:
 *   window.__setRoles(['gestor'])                  → financeiro some
 *   window.__setRoles(['gestor','finance_admin'])  → financeiro aparece
 *
 * Só esconde/mostra UI. O RLS continua decidindo o que o banco devolve, então
 * conceder-se `finance_admin` aqui não revela nenhum custo.
 */
const DEV_ROLES_KEY = 'sistemas:dev:roles'

function readRoleOverride(): Role[] | null {
  if (!import.meta.env.DEV) return null
  try {
    const raw = window.localStorage.getItem(DEV_ROLES_KEY)
    return raw ? (JSON.parse(raw) as Role[]) : null
  } catch {
    return null
  }
}

/**
 * Resolve a ficha do departamento.
 *
 * A consulta passa pelo RLS como qualquer outra: `member_read_directory` exige
 * que a sessão já corresponda a um membro ativo. Quem não tem ficha lê zero
 * linhas — e é assim que se detecta "logou no SSO mas não é do Sistemas", sem
 * precisar de função extra.
 */
async function resolveMembership(userId: string): Promise<Resolved> {
  const sb = getSupabase()

  const { data: member, error: memberError } = await sb
    .from('member')
    .select('id, full_name, email, is_active')
    .eq('user_id', userId)
    .maybeSingle()

  if (memberError) throw new Error(memberError.message)
  if (!member || member.is_active !== true) {
    return { status: 'sem-acesso', member: null, roles: [] }
  }

  const { data: roleRows, error: rolesError } = await sb
    .from('member_role')
    .select('role')
    .eq('member_id', member.id as string)

  if (rolesError) throw new Error(rolesError.message)

  const roles = ((roleRows ?? []) as Array<{ role: Role }>).map((r) => r.role)

  return {
    status: 'autenticado',
    member: {
      id: member.id as string,
      displayName: member.full_name as string,
      email: member.email as string,
    },
    roles: readRoleOverride() ?? roles,
  }
}

/**
 * Provider e gate ao mesmo tempo: nenhum filho renderiza sem sessão resolvida.
 *
 * Deliberado — se o gate fosse opcional, bastaria uma rota nova esquecer de
 * aplicá-lo para expor tela sem autenticação.
 */
export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [resolved, setResolved] = React.useState<Resolved>({
    status: 'carregando',
    member: null,
    roles: [],
  })
  const [attempt, setAttempt] = React.useState(0)

  React.useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        if (appEnv.authMode === 'local') {
          const userId = await ensureDevLogin()
          if (!userId) {
            if (!cancelled) {
              setResolved({
                status: 'erro',
                member: null,
                roles: [],
                detail:
                  'Modo local ativo, mas faltam VITE_DEV_SUPABASE_EMAIL e VITE_DEV_SUPABASE_PASSWORD no .env.local.',
              })
            }
            return
          }

          const next = await resolveMembership(userId)
          if (cancelled) return
          if (next.status === 'autenticado') clearRepairAttempt()
          setResolved(next)
          return
        }

        const session = await fetchBridgeSession()

        if (!session) {
          if (!cancelled) setResolved({ status: 'anonimo', member: null, roles: [] })
          return
        }

        if (!session.supabaseAccessToken) {
          // Autenticado no SSO, mas sem token de dados: o app foi registrado sem
          // `requiresSupabaseAccessToken`, ou falta membership no portal. Toda
          // query voltaria vazia e a tela pareceria apenas "sem dados" — melhor
          // dizer o que realmente é.
          if (!cancelled) {
            setResolved({
              status: 'sem-acesso',
              member: null,
              roles: [],
              detail:
                'O SSO autenticou, mas não emitiu o token de acesso aos dados. Verifique o cadastro do app `sistemas` no portal.',
            })
          }
          return
        }

        const next = await resolveMembership(session.userId)
        if (cancelled) return

        if (next.status === 'autenticado') clearRepairAttempt()
        setResolved(next)
      } catch (error) {
        if (!cancelled) {
          setResolved({
            status: 'erro',
            member: null,
            roles: [],
            detail: error instanceof Error ? error.message : 'Falha desconhecida',
          })
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [attempt])

  // Anônimo entra no handoff do SSO. A guarda de logout evita o clássico
  // "cliquei em sair e voltei logado": entre o POST /auth/logout e a navegação
  // existe um render em que a sessão já não existe.
  React.useEffect(() => {
    if (appEnv.authMode === 'local') return
    if (resolved.status === 'anonimo' && !getIsSigningOut()) {
      redirectToBridgeLogin(window.location.href)
    }
  }, [resolved.status])

  React.useEffect(() => {
    if (!import.meta.env.DEV) return
    const w = window as unknown as { __setRoles?: (r: Role[]) => void }
    w.__setRoles = (next) => {
      window.localStorage.setItem(DEV_ROLES_KEY, JSON.stringify(next))
      setResolved((current) => ({ ...current, roles: next }))
    }
    return () => {
      delete w.__setRoles
    }
  }, [])

  const signOut = React.useCallback(async () => {
    if (appEnv.authMode === 'local') {
      await devSignOut()
      window.location.reload()
      return
    }
    const logoutUrl = await signOutFromBridge()
    window.location.assign(logoutUrl ?? '/login')
  }, [])

  const value = React.useMemo<SessionState>(
    () => ({
      status: resolved.status,
      member: resolved.member,
      roles: resolved.roles,
      signOut,
    }),
    [resolved, signOut],
  )

  if (resolved.status === 'carregando') {
    return (
      <FullScreen>
        <Loading label="Verificando sessão..." />
      </FullScreen>
    )
  }

  if (resolved.status === 'anonimo') {
    return (
      <FullScreen>
        <Loading label="Redirecionando para o login..." />
      </FullScreen>
    )
  }

  if (resolved.status === 'erro') {
    return (
      <FullScreen>
        <ErrorState
          title="Não foi possível carregar a sessão"
          description={
            resolved.detail ??
            'O Hub não conseguiu falar com o SSO ou com o banco. Se persistir, avise o time de Sistemas.'
          }
          onRetry={() => {
            setResolved({ status: 'carregando', member: null, roles: [] })
            setAttempt((n) => n + 1)
          }}
        />
      </FullScreen>
    )
  }

  if (resolved.status === 'sem-acesso') {
    return (
      <FullScreen>
        <Forbidden
          description={
            resolved.detail ??
            'Sua conta entrou no SSO, mas ainda não tem ficha ativa no Departamento de Sistemas. Peça a liberação ao gestor do departamento.'
          }
        />
      </FullScreen>
    )
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md">{children}</div>
    </div>
  )
}

export function useSession(): SessionState {
  const context = React.useContext(SessionContext)
  if (!context) {
    throw new Error('useSession precisa estar dentro de <SessionProvider>')
  }
  return context
}
